import { useState } from 'react';
import { motion } from 'framer-motion';
import { useRoulette } from '../../../hooks/useRoulette';
import { useWalletStore } from '../../../store/walletStore';
import { BettingBoard, type RouletteSelection } from './BettingBoard';

export function Roulette({ minBet, maxBet }: { minBet: number; maxBet: number }) {
  const { placeBet, spinning, result, error } = useRoulette();
  const balance = useWalletStore((s) => s.balance);
  const [bet, setBet] = useState(minBet || 10);
  const [selected, setSelected] = useState<RouletteSelection | null>(null);

  const canSpin =
    !spinning &&
    selected !== null &&
    balance !== null &&
    bet >= minBet &&
    bet <= maxBet &&
    bet <= balance;

  const onSpin = async () => {
    if (!selected) return;
    try {
      await placeBet(bet, selected.betType, selected.betValue);
    } catch {
      /* surfaced via hook */
    }
  };

  return (
    <div className="space-y-6">
      <div className="card space-y-4">
        <div className="flex items-center justify-center gap-4">
          <motion.div
            className="flex h-20 w-20 items-center justify-center rounded-full border-4 border-gold bg-felt-dark text-3xl font-bold text-gold"
            animate={spinning ? { rotate: 360 } : { rotate: 0 }}
            transition={spinning ? { repeat: Infinity, duration: 0.6, ease: 'linear' } : {}}
          >
            {spinning ? '🎡' : (result?.winningNumber ?? '—')}
          </motion.div>
          {result && !spinning && (
            <p
              className={`text-lg font-bold ${
                result.payout > 0 ? 'text-emerald-400' : 'text-slate-400'
              }`}
            >
              {result.payout > 0
                ? `Landed on ${result.winningNumber} — you won ${result.payout}!`
                : `Landed on ${result.winningNumber} — no win.`}
            </p>
          )}
        </div>
      </div>

      <div className="card">
        <BettingBoard
          selected={selected}
          onSelect={setSelected}
          winningNumber={spinning ? null : (result?.winningNumber ?? null)}
        />
      </div>

      <div className="card flex flex-wrap items-center gap-3">
        <label className="label mb-0">Bet</label>
        <input
          type="number"
          className="input w-28"
          min={minBet}
          max={maxBet}
          value={bet}
          onChange={(e) => setBet(Math.floor(Number(e.target.value)))}
        />
        <span className="text-sm text-slate-400">
          {selected ? `On: ${selected.label}` : 'Pick a bet on the board'}
        </span>
        <button className="btn-gold ml-auto" disabled={!canSpin} onClick={onSpin}>
          {spinning ? 'Spinning…' : 'Spin'}
        </button>
      </div>
      {error && <p className="text-sm text-rose-400">{error}</p>}
    </div>
  );
}
